import { useCallback, useEffect, useRef, useState } from "react";
import {
  adaptVideo,
  getCatalog,
  getFeed,
  getVideo,
  getContentMenu,
  getCustomNavigation,
  IS_DEMO,
  type CustomNavigation,
} from "./api";
import { videos, type VideoItem } from "./data";
const message = (error: unknown) =>
  error instanceof Error ? error.message : "读取失败，请重试";
export function useCatalog() {
  const [items, setItems] = useState<VideoItem[]>(IS_DEMO ? videos : []);
  const [loading, setLoading] = useState(!IS_DEMO);
  const [moreLoading, setMoreLoading] = useState(false);
  const [error, setError] = useState("");
  const [detailError, setDetailError] = useState("");
  const [detailLoading, setDetailLoading] = useState(false);
  const [total, setTotal] = useState(IS_DEMO ? videos.length : 0);
  const [cursor, setCursor] = useState<string | null>(null);
  const [menu, setMenu] = useState<{ types: string[]; labels: string[] }>({
    types: [],
    labels: [],
  });
  const [menuError, setMenuError] = useState("");
  const [customNavigation, setCustomNavigation] =
    useState<CustomNavigation | null>(null);
  const [navigationError, setNavigationError] = useState("");
  const [navigationLoading, setNavigationLoading] = useState(!IS_DEMO);
  const [navigationAttempt, setNavigationAttempt] = useState(0);
  const request = useRef<AbortController | null>(null);
  const busy = useRef(false);
  const details = useRef(new Map<string, VideoItem>());
  const activeDetail = useRef("");
  const selectedVideo = useRef<VideoItem | null>(null);
  const selectVideo = useCallback((item: VideoItem) => {
    selectedVideo.current = item;
    setItems((existing) =>
      existing.some((v) => v.id === item.id)
        ? existing
        : [details.current.get(item.id) || item, ...existing],
    );
  }, []);
  const detailRequest = useRef<AbortController | null>(null);
  const load = useCallback(async (next = "") => {
    if (IS_DEMO || busy.current) return;
    busy.current = true;
    setError("");
    next ? setMoreLoading(true) : setLoading(true);
    const controller = new AbortController();
    request.current = controller;
    try {
      const response = await getCatalog(next, controller.signal);
      if (controller.signal.aborted) return;
      const player = next ? null : await getFeed("", controller.signal);
      if (controller.signal.aborted) return;
      const rows = [
        ...response.items,
        ...(player?.items || []).filter(
          (v) => !response.items.some((r) => r.id === v.id),
        ),
      ];
      const mapped = rows.map(
        (v) => details.current.get(v.id) || adaptVideo(v),
      );
      // Keep a card selection / direct link even when it is outside this catalog page.
      const selected =
        selectedVideo.current || details.current.get(activeDetail.current);
      if (selected && !mapped.some((v) => v.id === selected.id))
        mapped.unshift(details.current.get(selected.id) || selected);
      setItems((existing) =>
        next
          ? [
              ...existing,
              ...mapped.filter((v) => !existing.some((e) => e.id === v.id)),
            ]
          : mapped,
      );
      setTotal(response.total);
      setCursor(response.hasMore ? response.nextCursor : null);
    } catch (error) {
      if (!controller.signal.aborted) setError(message(error));
    } finally {
      if (request.current === controller) {
        busy.current = false;
        setLoading(false);
        setMoreLoading(false);
      }
    }
  }, []);
  const loadDetail = useCallback(async (id: string) => {
    if (IS_DEMO || !id || id.startsWith("local-")) return;
    activeDetail.current = id;
    detailRequest.current?.abort();
    setDetailError("");
    const cached = details.current.get(id);
    if (cached) {
      setDetailLoading(false);
      setItems((s) =>
        s.map((v) =>
          v.id === id
            ? { ...cached, src: v.src || cached.src, episode: v.episode }
            : v,
        ),
      );
      return;
    }
    const controller = new AbortController();
    detailRequest.current = controller;
    setDetailLoading(true);
    try {
      const item = adaptVideo(await getVideo(id, controller.signal));
      if (controller.signal.aborted) return;
      details.current.set(id, item);
      setItems((s) =>
        s.some((v) => v.id === id)
          ? s.map((v) => (v.id === id ? item : v))
          : [item, ...s],
      );
    } catch (error) {
      if (!controller.signal.aborted && activeDetail.current === id)
        setDetailError(message(error));
    } finally {
      if (!controller.signal.aborted && activeDetail.current === id)
        setDetailLoading(false);
    }
  }, []);
  useEffect(() => {
    void load();
    return () => {
      request.current?.abort();
      detailRequest.current?.abort();
      busy.current = false;
    };
  }, [load]);
  useEffect(() => {
    if (IS_DEMO) return;
    const controller = new AbortController();
    getContentMenu(controller.signal)
      .then(setMenu)
      .catch(() => {
        if (!controller.signal.aborted)
          setMenuError("分类配置暂时无法读取，先按视频类型查看");
      });
    return () => controller.abort();
  }, []);
  useEffect(() => {
    if (IS_DEMO) return;
    const controller = new AbortController();
    setNavigationLoading(true);
    setNavigationError("");
    getCustomNavigation(controller.signal)
      .then((value) => {
        if (!controller.signal.aborted) setCustomNavigation(value);
      })
      .catch((error) => {
        if (!controller.signal.aborted) setNavigationError(message(error));
      })
      .finally(() => {
        if (!controller.signal.aborted) setNavigationLoading(false);
      });
    return () => controller.abort();
  }, [navigationAttempt]);
  return {
    items,
    setItems,
    selectVideo,
    loading,
    moreLoading,
    error,
    detailError,
    detailLoading,
    total,
    menu,
    menuError,
    customNavigation,
    navigationError,
    navigationLoading,
    retryNavigation: () => setNavigationAttempt((value) => value + 1),
    hasMore: !!cursor,
    loadMore: () => void load(cursor || ""),
    retry: () => void load(),
    loadDetail,
  };
}
